import React from 'react'
import classNames from 'classnames'
import { FlexColumn } from '@/atoms/FlexContainers'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { CaptionSM, LabelMD, TitleMD } from '@/atoms/Text'
import { IconColor } from '@/atoms/utils'
import { paths } from '@/constants/paths'
import { SocialShare, useSocialSharing } from '@/organisms/SocialSharing'
import { useTranslate } from '@/utils/translate/translate-client'

interface SocialSharingProps {
  color?: IconColor
  shareUrl?: string
  vertical?: boolean
  onShare?: (network: string) => void
  iconWrapperClassName?: string
  title?: string
  copyLinkClass?: string
  emailContent?: {
    subject: string
    body: string
  }
}

export const SocialSharing: React.FC<SocialSharingProps> = ({
  color = 'gray-700',
  shareUrl = encodeURI(paths.getBaseURL()),
  onShare,
  iconWrapperClassName,
  title,
  copyLinkClass,
  emailContent,
}) => {
  const { links, socials } = useSocialSharing({ shareUrl, emailContent })
  const { t } = useTranslate('common')

  const renderSocialIcon = ({ name, href, onClick, icon: Icon }: SocialShare) => {
    if (name === 'Copy Link') return null

    return (
      <div className="w-[66px]">
        <a
          key={name}
          className={classNames(
            'focus-visible:right-offset-0 flex flex-col items-center gap-2 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent',
            iconWrapperClassName,
          )}
          href={href}
          rel="noreferrer"
          target="_blank"
          onClick={() => {
            onShare?.(name)
            onClick?.()
          }}
          aria-label={name}
        >
          <div className="rounded-full bg-black p-4">
            <Icon color={'white'} size={24} />
          </div>
          <div>
            <CaptionSM> {name}</CaptionSM>
          </div>
        </a>
      </div>
    )
  }

  return (
    <div>
      <FlexColumn className="gap-8 py-6 md:pb-8">
        <FlexColumn className="gap-2 text-left">
          <TitleMD weight={'semibold'}>{title ? title : t('socialShare', 'Share')}</TitleMD>
        </FlexColumn>
      </FlexColumn>
      <LabelMD weight={'medium'} color={'gray-700'} className={'pb-4 text-left'}>
        Sharing Options
      </LabelMD>
      <FlexColumn className="mb-6 gap-8">
        <div className="grid grid-cols-6 gap-x-2">
          {links.filter((link) => link.name !== 'Copy Link').map(renderSocialIcon)}
        </div>
      </FlexColumn>
      <FlexColumn className={classNames('gap-8', copyLinkClass)}>
        <div className="mt-2">
          <LabelMD className="mb-2 text-left text-sm">{t('pageLink', 'Page Link')}</LabelMD>
          <div className="flex items-center rounded-[14px] border border-gray-400 bg-gray-100/20 p-3">
            <input type="text" value={shareUrl} readOnly className="grow bg-transparent text-sm outline-none" />
            <button
              onClick={() => {
                socials.copyLink.onClick?.()
              }}
              className="ml-2 rounded p-1 hover:bg-gray-200"
              aria-label="Copy link"
            >
              <CopyIcon size={20} color={color} />
            </button>
          </div>
        </div>
      </FlexColumn>
    </div>
  )
}
